import * as types from '../mutationTypes';
import { dayjs } from '~/plugins/dayjs';
import { kpwebFetch } from '~/plugins/customFetch';

import {
  findFactoryAmongNavigationData,
  findGroupAmongNavigationData,
  findMachineAmongNavigationData,
} from '~/helpers/navigation';
import { useIsa95EquipmentsRef } from '~/composables/useEquipment';

// In-flight fetchNavigation requests, keyed by navigation type.
const pendingNavigationFetches = new Map();

// state
export const state = {
  period_value: {
    Day: dayjs()
      .format('YYYY-MM-DD'),
    Week: [
      dayjs()
        .startOf('week')
        .format('YYYY-MM-DD'),
      dayjs()
        .endOf('week')
        .format('YYYY-MM-DD')],
    Month: dayjs()
      .format('YYYY-MM'),
    Year: dayjs()
      .format('YYYY'),
    Custom: [
      dayjs()
        .startOf('week')
        .format('YYYY-MM-DD'),
      dayjs()
        .format('YYYY-MM-DD')],
  },
  periodTypeSelected: 'Day',
  // Tells if dashboard navigation is showed on tablet/mobile devices
  showNavigation: false,
  // Id of the selected machine in navigation
  machineSelected: null,
  // Timezone of the selected machine in navigation
  machineTimezone: 'GMT',
  groupSelected: null,
  factorySelected: null,
  selectedManufacturingOrder: null,
  selectedChangeover: null,
  // Tells if we are about to search or currently fetching for changeovers from API
  fetchingChangeovers: false,
  // Do we display the shortcuts (Create event/Create changeover) in DefaultMenu
  showShortcuts: false,
  // Store the changeovers that are eventually displayed in navigation
  changeovers: [],
  // A key that you can refresh if you want to refresh the navigation component
  navigationKey: 0,
  // Will store all the raw navigation data coming from API
  navigationData: [],
  openingHours: null,
  groupsSelected: [],
  machinesSelected: [],
  // nav-next (E-0102) — keys written only when the new navigation is active.
  // Resolved period bounds published by useProductionPeriod: naive UTC strings,
  // exclusive end [start, end); `source` discriminates the writer ('nav-next').
  resolvedPeriod: null,
  // Day-mode time range: { kind: 'FULL_DAY'|'PRODUCTION_DAY'|'SHIFT', periods: [] }
  selectedTimeRange: null,
  // How widget bounds treat a selected manufacturing order: 'TRUNCATED'|'FULL'
  manufacturingOrderScope: 'TRUNCATED',
  // Identity of the selected OF's changeover, published by the nav-next OF row:
  // { changeoverId, eventId, moCode, phaseStatus }. nav-next deliberately does
  // NOT fill the legacy `changeovers` list (its OF query stays lean, and the
  // resolution funnel already narrows the bounds), so the consumers that need
  // only the SELECTED changeover read this instead — see useChangeover.
  selectedChangeoverSummary: null,
  // Production-day settings keyed by factory id:
  // { [factoryId]: { firstDayOfWeek, startProductionTime, startProductionDayOffset, timezone } }
  factorySettings: {},
  // Global custom hours of the period, applied by the modal (S-0016), all types:
  // { enabled, startTime, endTime } or null. Read by useProductionPeriod.
  periodCustomHours: null,
  // Sticky RELATIVE period (S-0025): key of the shortcut whose window is
  // active ('yesterday', 'last_7_days'…) — re-resolved at each page load by
  // the URL sync, survives dashboard changes. null = absolute selection.
  activeShortcut: null,
};

// getters
export const getters = {
  period_value: (state) => state.period_value,
  periodTypeSelected: (state) => state.periodTypeSelected,
  // eslint-disable-next-line no-shadow
  selectedSource: (state, getters) => {
    const { machineSelected } = getters;
    const machineDevices = machineSelected?.machine_devices;
    if (Array.isArray(machineDevices) && machineDevices.length > 0) {
      return 'device';
    }

    return 'machine';
  },
  changeovers: (state) => state.changeovers,
  // Selected factory in navigation (whole object)
  factorySelected: (state) => findFactoryAmongNavigationData(
    state.factorySelected,
    state.navigationData,
  ),
  // Selected group in navigation (whole object)
  groupSelected: (state) => findGroupAmongNavigationData(
    state.groupSelected,
    state.navigationData,
  ),
  // Selected group ids in navigation.
  groupsSelected: (state) => state.groupsSelected,

  // Selected machine in navigation (whole object)
  machineSelected: (state) => findMachineAmongNavigationData(
    state.machineSelected,
    state.navigationData,
    useIsa95EquipmentsRef.value,
  ),
  // Selected machine id (raw state) — unlike machineSelected, it does not
  // depend on navigationData being loaded. Read by nav-next (E-0102).
  machineSelectedId: (state) => state.machineSelected,
  openingHours: (state) => state.openingHours,

  // Expanded navigation state.
  getShowNavigation: (state) => state.showNavigation,

  // Selected changeover in navigation
  getSelectedChangeover: (state) => state.selectedChangeover,

  // nav-next (E-0102)
  resolvedPeriod: (state) => state.resolvedPeriod,
  selectedTimeRange: (state) => state.selectedTimeRange,
  selectedChangeoverSummary: (state) => state.selectedChangeoverSummary,
  manufacturingOrderScope: (state) => state.manufacturingOrderScope,
  factorySettings: (state) => state.factorySettings,
  periodCustomHours: (state) => state.periodCustomHours,
  activeShortcut: (state) => state.activeShortcut,
};

// mutations
export const mutations = {
  [types.SET_PERIOD_VALUE](state, value) {
    state.period_value[state.periodTypeSelected] = value;
  },
  [types.SET_PERIOD_TYPE_SELECTED](state, value) {
    state.periodTypeSelected = value;
  },
  setShowNavigation(state, b) {
    state.showNavigation = b;
  },
  setMachineSelected(state, data) {
    state.machineSelected = data;
  },
  setMachineTimezone(state, timezone) {
    state.machineTimezone = timezone;
  },
  setGroupSelected(state, data) {
    state.groupSelected = data;
  },
  setGroupsSelected(state, data) {
    state.groupsSelected = data;
  },
  setMachinesSelected(state, data) {
    state.machinesSelected = data;
  },
  setFactorySelected(state, data) {
    state.factorySelected = data;
  },
  [types.SET_MANUFACTURING_ORDER](state, payload) {
    state.selectedManufacturingOrder = payload;
  },
  setSelectedChangeover(state, payload) {
    state.selectedChangeover = payload;
  },
  setFetchingChangeovers(state, payload) {
    state.fetchingChangeovers = payload;
  },
  setShowShortcuts(state, payload) {
    state.showShortcuts = payload;
  },
  saveChangeovers(state, payload) {
    state.changeovers = payload;
  },
  /**
   * Call this mutation if you want to refresh the navigation component
   */
  refreshNavigation(state) {
    state.navigationKey += 1;
  },
  setNavigationData(state, payload) {
    state.navigationData = payload;
  },
  /**
   * Call this mutation if you want to reset navigation period value
   * @param state
   */
  resetPeriodValue(state) {
    state.period_value = {
      Day: dayjs()
        .format('YYYY-MM-DD'),
      Week: [
        dayjs()
          .startOf('week')
          .format('YYYY-MM-DD'),
        dayjs()
          .endOf('week')
          .format('YYYY-MM-DD'),
      ],
      Month: dayjs()
        .format('YYYY-MM'),
      Year: dayjs()
        .format('YYYY'),
      Custom: [
        dayjs()
          .startOf('week')
          .format('YYYY-MM-DD'),
        dayjs()
          .format('YYYY-MM-DD'),
      ],
    };
  },
  commitOpeningHours(state, payload) {
    state.openingHours = payload;
  },
  // nav-next (E-0102)
  setResolvedPeriod(state, payload) {
    state.resolvedPeriod = payload;
  },
  setSelectedTimeRange(state, payload) {
    state.selectedTimeRange = payload;
  },
  setManufacturingOrderScope(state, payload) {
    state.manufacturingOrderScope = payload;
  },
  setSelectedChangeoverSummary(state, payload) {
    state.selectedChangeoverSummary = payload;
  },
  setFactorySettings(state, payload) {
    state.factorySettings = payload;
  },
  setPeriodCustomHours(state, payload) {
    state.periodCustomHours = payload;
  },
  setActiveShortcut(state, payload) {
    state.activeShortcut = payload;
  },
};
// actions
export const actions = {
  /**
   * Commit a period draft applied from the modal (S-0016) in a single action:
   * type FIRST (SET_PERIOD_VALUE writes into period_value[periodTypeSelected]),
   * then value, then the global custom hours. resolvedPeriod is NOT written
   * here — useProductionPeriod is its single writer and recomputes on its watch.
   */
  applyPeriodDraft({ commit }, {
    type, value, customHours = null, activeShortcut = null,
  }) {
    commit(types.SET_PERIOD_TYPE_SELECTED, type);
    commit(types.SET_PERIOD_VALUE, value);
    commit('setPeriodCustomHours', customHours?.enabled ? { ...customHours } : null);
    // S-0025: a shortcut apply arms the sticky relative mode; any manual
    // apply (activeShortcut null) leaves it.
    commit('setActiveShortcut', activeShortcut);
  },
  /**
   * Fetch the navigation from API. Concurrent calls for the same type share
   * the in-flight request (e.g. the nav-next mounted twice, desktop + tablet).
   * @param context
   * @param navigationType
   * @returns {Promise<Array>}
   */
  fetchNavigation(context, navigationType = 'machine', bypassCache = false) {
    if (pendingNavigationFetches.has(navigationType)) {
      return pendingNavigationFetches.get(navigationType);
    }

    const request = (async () => {
      const url = `/api/navigation/${navigationType}`;
      try {
        const response = await kpwebFetch(url, {
          headers: { cache: bypassCache ? '0' : '1', Accept: 'application/json' },
        });

        // If the request failed (e.g. 4xx/5xx), return an empty array to keep callers safe.
        const data = response?.ok ? await response.json().catch(() => []) : [];

        context.commit('setNavigationData', data);

        return data;
      } catch (error) {
        console.error('Error fetching navigation:', error);
        context.commit('setNavigationData', []);
        return [];
      } finally {
        pendingNavigationFetches.delete(navigationType);
      }
    })();

    pendingNavigationFetches.set(navigationType, request);

    return request;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
